import { useCallback, useState } from 'react';
import { FUNDRAISING_SORT_KEYS } from 'features/IRWorkstation/constants/fundraisingColumns';

const SORT_STORAGE_KEY = 'fundraisingPipeline:sort';

const EMPTY_SORT = { sortBy: null, sortDir: 'asc' };

// A stored column that no longer exists (or a corrupt entry) falls back to the
// API default ordering rather than sending an unknown column.
const readStoredSort = (sortKeys, storageKey) => {
	try {
		const stored = JSON.parse(localStorage.getItem(storageKey));

		if (!sortKeys.includes(stored?.sortBy)) return EMPTY_SORT;

		return {
			sortBy: stored.sortBy,
			sortDir: stored.sortDir === 'desc' ? 'desc' : 'asc',
		};
	} catch {
		return EMPTY_SORT;
	}
};

// Sort state for a fundraising table (the LP Pipeline by default; the LP360
// table passes its own column keys and storage key). Persisted in localStorage
// so it survives navigation and reloads, matching how the Active Raise panel
// pins an entity.
const useFundraisingSort = (
	sortKeys = FUNDRAISING_SORT_KEYS,
	storageKey = SORT_STORAGE_KEY
) => {
	const [sort, setSort] = useState(() => readStoredSort(sortKeys, storageKey));

	// The grid reports the column and direction it wants (and an empty model when
	// the sort is cleared), so the sort is set outright rather than toggled.
	const applySort = useCallback(
		(sortModel) => {
			const [next] = sortModel || [];

			if (!next || !sortKeys.includes(next.field)) {
				setSort(EMPTY_SORT);
				localStorage.removeItem(storageKey);
				return;
			}

			const value = {
				sortBy: next.field,
				sortDir: next.sort === 'desc' ? 'desc' : 'asc',
			};

			setSort(value);
			localStorage.setItem(storageKey, JSON.stringify(value));
		},
		[sortKeys, storageKey]
	);

	return { sort, applySort };
};

export { SORT_STORAGE_KEY };
export default useFundraisingSort;
